import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import School from '@mui/icons-material/School';
import { Users } from 'lucide-react';
import { Course, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface CoursesManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';
import { CourseStudentList } from './CourseStudentList';

export const CoursesManagement: React.FC<CoursesManagementProps> = ({ permissions }) => {
  const { courses, setCourses, schoolYears } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [viewingCourse, setViewingCourse] = useState<Course | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<Course>>({
    name: '',
    code: '',
    description: '',
    department: '',
    isActive: true,
    schoolYearIds: [],
  });

  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const matchesSearch =
        course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (course.department?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      return matchesSearch;
    });
  }, [courses, searchQuery]);

  const handleAdd = () => {
    setEditingCourse(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      department: '',
      isActive: true,
      schoolYearIds: [],
    });
    setOpenDialog(true);
  };

  const handleEdit = (course: Course) => {
    setEditingCourse(course);
    setFormData(course);
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      setCourses(courses.filter((c) => c.id !== id));
      const index = mockCourses.findIndex((c) => c.id === id);
      if (index > -1) {
        mockCourses.splice(index, 1);
      }
    }
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.code || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }

    if (editingCourse) {
      const updatedCourses = courses.map((c) =>
        c.id === editingCourse.id ? { ...c, ...formData } : c
      );
      setCourses(updatedCourses);
      const index = mockCourses.findIndex((c) => c.id === editingCourse.id);
      if (index > -1) {
        mockCourses[index] = { ...mockCourses[index], ...formData };
      }
    } else {
      const newCourse: Course = {
        id: uuidv4(),
        name: formData.name!,
        code: formData.code!,
        description: formData.description!,
        department: formData.department,
        isActive: formData.isActive ?? true,
        schoolYearIds: formData.schoolYearIds,
      };
      setCourses([...courses, newCourse]);
      mockCourses.push(newCourse);
    }

    setOpenDialog(false);
  };

  if (viewingCourse) {
    return (
      <CourseStudentList
        course={viewingCourse}
        onBack={() => setViewingCourse(null)}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Courses Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage courses available for fee assignment
          </Typography>
        </Box>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              bgcolor: '#1e293b',
              '&:hover': {
                bgcolor: '#0f172a',
              },
              flexShrink: 0,
            }}
          >
            Add Course
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search courses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(auto-fill, minmax(300px, 1fr))',
          },
          gap: { xs: 2, sm: 2 },
        }}
      >
        {filteredCourses.map((course) => (
          <Box key={course.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Box
                    sx={{
                      bgcolor: '#1e293b',
                      borderRadius: '8px',
                      p: 1,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <School sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                      {course.name}
                    </Typography>
                    <Chip
                      label={course.code}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        bgcolor: 'primary.lighter',
                        color: 'primary.main',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Chip
                    label={course.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={course.isActive ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.75rem', ml: 1 }}
                  />
                </Box>
                {course.department && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      display: 'block',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    {course.department}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    mb: course.schoolYearIds && course.schoolYearIds.length > 0 ? 2 : 0,
                  }}
                >
                  {course.description}
                </Typography>
                {course.schoolYearIds && course.schoolYearIds.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
                    >
                      School Years
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {schoolYears
                        .filter((y) => course.schoolYearIds?.includes(y.id))
                        .map((year) => (
                          <Chip
                            key={year.id}
                            label={year.name}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(25, 118, 210, 0.08)',
                              color: 'primary.main',
                            }}
                          />
                        ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Users size={16} />}
                  onClick={() => setViewingCourse(course)}
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    bgcolor: '#1e293b',
                    '&:hover': {
                      bgcolor: '#0f172a',
                    },
                  }}
                >
                  Students
                </Button>
                {permissions.update && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(course)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(course.id)}
                    color="error"
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {filteredCourses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No courses found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add Course" to create your first course'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCourse ? 'Edit Course' : 'Add New Course'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Course Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Course Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
              helperText="e.g., MATH101, ELA201"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              required
            />
            <TextField
              label="Department"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              fullWidth
              helperText="Optional - e.g., Mathematics, Science, Arts"
            />
            <Autocomplete
              multiple
              options={schoolYears.filter(y => y.isActive)}
              getOptionLabel={(option) => option.name}
              value={schoolYears.filter(y => formData.schoolYearIds?.includes(y.id))}
              onChange={(_, newValue) => {
                setFormData({ ...formData, schoolYearIds: newValue.map(y => y.id) });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="School Years (Optional)"
                  placeholder="Select school years..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={option.name}
                      {...tagProps}
                      size="small"
                    />
                  );
                })
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#1e293b',
              '&:hover': {
                bgcolor: '#0f172a',
              },
            }}
          >
            {editingCourse ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
